import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Save, KeyRound } from "lucide-react";

const FIELDS = [
  { k: "night_surcharge", label: "Adicional por turno nocturno ($/hora)", testid: "budget-night-surcharge" },
  { k: "weekend_surcharge", label: "Adicional por fin de semana ($/hora)", testid: "budget-weekend-surcharge" },
  { k: "holiday_surcharge", label: "Adicional por feriado ($/hora)", testid: "budget-holiday-surcharge" },
  { k: "social_charges_pct", label: "Cargas sociales (%)", testid: "budget-social-pct" },
  { k: "admin_expenses_pct", label: "Gastos administrativos (%)", testid: "budget-admin-pct" },
  { k: "supervision_pct", label: "Supervisión (%)", testid: "budget-supervision-pct" },
  { k: "margin_pct", label: "Margen (%)", testid: "budget-margin-pct" },
  { k: "iva_pct", label: "IVA (%)", testid: "budget-iva-pct" },
];

export function SettingsTab({ token }) {
  const headers = { "X-Budget-Pin-Token": token };
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinBusy, setPinBusy] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/budget/settings", { headers }); setForm(data); }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, []); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      delete payload.last_costs;
      FIELDS.forEach((f) => { payload[f.k] = Number(payload[f.k]) || 0; });
      const { data } = await api.patch("/budget/settings", payload, { headers });
      setForm(data);
      toast.success("Parámetros actualizados");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const changePin = async (e) => {
    e.preventDefault();
    if (newPin.length < 4) return toast.error("El PIN debe tener al menos 4 dígitos");
    if (newPin !== confirmPin) return toast.error("Los PIN no coinciden");
    setPinBusy(true);
    try {
      await api.patch("/budget/settings", { new_pin: newPin }, { headers });
      toast.success("PIN actualizado");
      setNewPin(""); setConfirmPin("");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setPinBusy(false); }
  };

  if (!form) return <div className="skel" style={{ height: 200 }} />;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 18, alignItems: "start" }}>
      <form onSubmit={save} className="card" style={{ padding: 18 }} data-testid="budget-settings-form">
        <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Parámetros de cálculo</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          {FIELDS.map((f) => (
            <div className="field" key={f.k}><label>{f.label}</label>
              <input type="number" min="0" step="0.01" value={form[f.k] ?? 0} onChange={(e) => set(f.k, e.target.value)} data-testid={f.testid}/>
            </div>
          ))}
          <div className="field"><label>Ventana horaria nocturna — desde</label>
            <input type="time" value={form.night_from || "22:00"} onChange={(e) => set("night_from", e.target.value)} data-testid="budget-night-from"/>
          </div>
          <div className="field"><label>Ventana horaria nocturna — hasta</label>
            <input type="time" value={form.night_to || "06:00"} onChange={(e) => set("night_to", e.target.value)} data-testid="budget-night-to"/>
          </div>
        </div>
        <p style={{ fontSize: 10, color: "var(--invict-text-muted)", margin: "8px 0 12px" }}>
          Un turno se considera "nocturno" si al menos la mitad de sus horas caen dentro de esta ventana. Los turnos que aplican solo sábado y/o domingo se consideran "fin de semana" (prioridad sobre nocturno). El valor de la hora ya no se define acá: se calcula en cada presupuesto a partir de sus costos reales (solapa "Simular presupuesto").
        </p>
        <button type="submit" disabled={busy} className="btn-primary" style={{ padding: "11px 20px" }} data-testid="budget-settings-save">
          <Save size={14} style={{ display: "inline", marginRight: 6 }}/>{busy ? "Guardando..." : "Guardar parámetros"}
        </button>
      </form>

      <form onSubmit={changePin} className="card" style={{ padding: 18 }} data-testid="budget-change-pin-form">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <KeyRound size={16} color="#BF3437"/><h3 style={{ margin: 0, fontSize: 14 }}>Cambiar PIN</h3>
        </div>
        <div className="field"><label>Nuevo PIN</label>
          <input type="password" inputMode="numeric" maxLength={8} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} data-testid="budget-change-pin-new"/>
        </div>
        <div className="field"><label>Confirmar nuevo PIN</label>
          <input type="password" inputMode="numeric" maxLength={8} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))} data-testid="budget-change-pin-confirm"/>
        </div>
        <button type="submit" disabled={pinBusy} className="btn-ghost" style={{ width: "100%", padding: 10 }} data-testid="budget-change-pin-submit">
          {pinBusy ? "Actualizando..." : "Actualizar PIN"}
        </button>
      </form>
    </div>
  );
}
